import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Linkedin, Twitter, Instagram, Globe, ArrowLeft } from 'lucide-react';
import Nav from '@/components/Nav';
import Footer from '@/components/Footer';
import StickyBottomMarquee, { STICKY_MARQUEE_SPACER_CLASS } from '@/components/StickyBottomMarquee';
import { api, fileUrl } from '@/lib/api';
import { MotionProvider, useReveal } from '@/lib/motion';
import MaintenancePage from '@/pages/MaintenancePage';

function SocialIcon({ href, label, Icon }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      className="w-10 h-10 rounded-full border-2 border-brand-orange text-brand-orange hover:bg-brand-orange hover:text-white motion-safe:transition-colors motion-safe:duration-200 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange focus-visible:ring-offset-2 focus-visible:ring-offset-bg-canvas"
      data-testid={`speaker-social-${label.toLowerCase()}`}
    >
      <Icon size={16} strokeWidth={2} />
    </a>
  );
}

export default function SpeakerDetail() {
  const { slug } = useParams();
  const [settings, setSettings] = useState({});
  const [bottomMarquee, setBottomMarquee] = useState({});
  const [speaker, setSpeaker] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error
  const headerReveal = useReveal();

  useEffect(() => {
    api.get('/public/home').then((r) => {
      setSettings(r.data?.settings || {});
      setBottomMarquee(r.data?.bottom_marquee || {});
    }).catch(() => {});
    setStatus('loading');
    api.get(`/speakers/${encodeURIComponent(slug)}`)
      .then((r) => {
        setSpeaker(r.data || null);
        setStatus('ok');
      })
      .catch((e) => {
        if (e?.response?.status === 404) setStatus('notfound');
        else setStatus('error');
      });
  }, [slug]);

  const maintenance = settings.maintenance || {};
  if (maintenance.enabled) return <MaintenancePage maintenance={maintenance} />;

  const motion = settings.motion || {};
  const nav = settings.nav || {};
  const footer = settings.footer || {};

  // Compute display flags only when speaker is loaded
  const linkedinUrl = speaker?.linkedin_url || speaker?.linkedin || '';
  const twitterUrl = speaker?.twitter_url || '';
  const instagramUrl = speaker?.instagram_url || '';
  const websiteUrl = speaker?.website_url || '';
  const anySocial = !!(linkedinUrl || twitterUrl || instagramUrl || websiteUrl);
  const showSocials = speaker?.show_social_links !== false && anySocial;
  const showBio = speaker?.show_bio !== false && (speaker?.bio || '').trim().length > 0;
  const showSession = speaker?.show_session_info !== false && (speaker?.session_info || '').trim().length > 0;

  return (
    <MotionProvider motion={motion}>
      <div className={`bg-bg-canvas min-h-screen flex flex-col ${STICKY_MARQUEE_SPACER_CLASS}`}>
        <Nav nav={nav} motion={motion} />
        <div className="sticky top-[72px] z-30 bg-bg-canvas border-b border-ink-primary/10" data-testid="page-back-bar">
          <div className="container-x py-3">
            <Link
              to="/speakers"
              className="inline-flex items-center gap-2 text-ink-primary/70 hover:text-ink-primary font-body text-[13px] md:text-[14px] min-h-[44px] motion-safe:transition-colors motion-safe:duration-200 focus:outline-none focus-visible:underline"
              data-testid="page-back-link"
            >
              <ArrowLeft size={14} aria-hidden="true" /> Back to All Speakers
            </Link>
          </div>
        </div>
        <main className="flex-1" data-testid="page-speaker-detail">
          <section className="section-pad relative">
            <div className="container-x">
              {/*
                The reveal-up wrapper is rendered UNCONDITIONALLY so that
                `headerReveal.ref` attaches to a real DOM node on the very
                first render. Previously the ref was attached to a div
                inside `{status === 'ok' && speaker && ...}`, which meant
                `useReveal`'s mount-time effect ran with `ref.current ===
                null` and bailed out without installing the
                IntersectionObserver. By the time the API resolved and the
                content div mounted, nothing was observing it — so
                `revealed` stayed `false`, `is-in` was never added, and
                the entire detail page rendered at opacity:0 (the user
                experienced this as "tapping a speaker card did nothing"
                because the destination page was invisible).
                The inner status branches below remain byte-identical
                to their previous shapes.
              */}
              <div
                ref={headerReveal.ref}
                className={`${headerReveal.simple ? 'reveal-simple' : 'reveal-up'} ${headerReveal.revealed ? 'is-in' : ''}`}
                data-testid="speaker-detail-reveal-wrapper"
              >
              {status === 'loading' && (
                <div className="max-w-5xl mx-auto" data-testid="speaker-detail-loading">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
                    <div className="rounded-md bg-ink-primary/10 animate-pulse" style={{ aspectRatio: '4/5' }} />
                    <div className="space-y-4">
                      <div className="h-10 bg-ink-primary/10 rounded animate-pulse w-3/4" />
                      <div className="h-5 bg-ink-primary/10 rounded animate-pulse w-2/3" />
                      <div className="h-4 bg-ink-primary/10 rounded animate-pulse w-full" />
                      <div className="h-4 bg-ink-primary/10 rounded animate-pulse w-5/6" />
                    </div>
                  </div>
                </div>
              )}

              {status === 'notfound' && (
                <div className="text-center max-w-2xl mx-auto" data-testid="speaker-detail-notfound">
                  <h1 className="font-display font-bold uppercase text-ink-primary leading-[0.95] text-[36px] md:text-[48px] tracking-tightish">
                    Speaker not found.
                  </h1>
                  <p className="mt-6">
                    <Link to="/speakers" className="text-brand-orange font-body font-semibold uppercase tracking-wider2 text-[13px] hover:underline" data-testid="speaker-back-link-404">
                      Back to all speakers
                    </Link>
                  </p>
                </div>
              )}

              {status === 'error' && (
                <p className="font-body text-[14px] text-ink-primary/70 text-center py-10" data-testid="speaker-detail-error">
                  Unable to load speaker. Please refresh the page.
                </p>
              )}

              {status === 'ok' && speaker && (
                <div className="max-w-5xl mx-auto" data-testid="speaker-detail-content">
                  {/* Hero: photo + name/title/socials */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
                    <div className="relative overflow-hidden rounded-md bg-card-dark" style={{ aspectRatio: '4/5' }} data-testid="speaker-detail-photo">
                      <img
                        src={fileUrl(speaker.photo) || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=900&q=80&auto=format&fit=crop'}
                        alt={speaker.name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0) 50%)' }} />
                    </div>

                    <div>
                      {(speaker.categories || []).length > 0 && (
                        <p className="font-display font-semibold uppercase text-brand-orange text-[12px] tracking-wider4" data-testid="speaker-categories">
                          {(speaker.categories || []).join(' · ')}
                        </p>
                      )}
                      <h1 className="mt-3 font-display font-bold uppercase text-ink-primary leading-[1.02] tracking-tightish text-[36px] md:text-[48px] lg:text-[60px]" data-testid="speaker-name">
                        {speaker.name}
                      </h1>
                      <p className="mt-4 font-body text-[15px] md:text-[16px] text-ink-primary/80 leading-relaxed" data-testid="speaker-title">
                        {speaker.title}
                      </p>

                      {showSocials && (
                        <div className="mt-6 flex items-center gap-3" data-testid="speaker-socials">
                          <SocialIcon href={linkedinUrl} label="LinkedIn" Icon={Linkedin} />
                          <SocialIcon href={twitterUrl} label="Twitter" Icon={Twitter} />
                          <SocialIcon href={instagramUrl} label="Instagram" Icon={Instagram} />
                          <SocialIcon href={websiteUrl} label="Website" Icon={Globe} />
                        </div>
                      )}
                    </div>
                  </div>

                  {showBio && (
                    <div className="mt-14" data-testid="speaker-bio-section">
                      <h2 className="font-display font-bold uppercase text-ink-primary leading-[1.05] tracking-tightish text-[24px] md:text-[28px]">
                        Bio
                      </h2>
                      <div className="mt-2 w-12 h-[3px] bg-brand-orange" aria-hidden="true" />
                      <div className="mt-5 max-w-[720px] font-body text-[15px] md:text-[16px] text-ink-primary/85 leading-[1.7] whitespace-pre-line" data-testid="speaker-bio-text">
                        {speaker.bio}
                      </div>
                    </div>
                  )}

                  {showSession && (
                    <div className="mt-12" data-testid="speaker-session-section">
                      <h2 className="font-display font-bold uppercase text-ink-primary leading-[1.05] tracking-tightish text-[24px] md:text-[28px]">
                        Session Info
                      </h2>
                      <div className="mt-2 w-12 h-[3px] bg-brand-orange" aria-hidden="true" />
                      <div className="mt-5 max-w-[720px] font-body text-[15px] md:text-[16px] text-ink-primary/85 leading-[1.7] whitespace-pre-line" data-testid="speaker-session-text">
                        {speaker.session_info}
                      </div>
                    </div>
                  )}

                  <div className="mt-16">
                    <Link
                      to="/speakers"
                      className="inline-flex items-center gap-2 text-brand-orange font-body font-semibold uppercase tracking-wider2 text-[13px] hover:underline focus:outline-none focus-visible:underline"
                      data-testid="speaker-back-link"
                    >
                      <ArrowLeft size={16} /> Back to all speakers
                    </Link>
                  </div>
                </div>
              )}
              </div>
            </div>
          </section>
        </main>
        <Footer footer={footer} motion={motion} presentingSponsor={settings.presenting_sponsor || null} />
        <StickyBottomMarquee bottomMarquee={bottomMarquee} />
      </div>
    </MotionProvider>
  );
}
